import { useState, useEffect, useRef } from 'react';
import { X, Plus, FloppyDisk, Clock, CaretDown } from '@phosphor-icons/react';
import { TemplateSelector } from './TemplateSelector';
import { ActionStepEditor } from './ActionStepEditor';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Every day', days: 'mon,tue,wed,thu,fri,sat,sun' },
  { value: 'weekdays', label: 'Weekdays', days: 'mon,tue,wed,thu,fri' },
  { value: 'weekends', label: 'Weekends', days: 'sat,sun' },
  { value: 'weekly', label: 'Weekly', days: 'mon' },
  { value: 'fortnightly', label: 'Fortnightly', days: 'mon', interval: 2 },
  { value: 'monthly', label: 'Monthly' },
];

const WEEKLY_DAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export const AgentBuilder = ({ templates, editingAgent, onSave, onClose, saving }) => {
  const [step, setStep] = useState(editingAgent ? 'configure' : 'choose');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actions, setActions] = useState([]);
  const [trigger, setTrigger] = useState({ type: 'cron', hour: 9, minute: 0, days: 'mon,tue,wed,thu,fri,sat,sun' });
  const [frequency, setFrequency] = useState('daily');

  // Derive frequency from trigger when editing
  const deriveFrequency = (t) => {
    if (t.day_of_month) return 'monthly';
    if (t.week_interval === 2) return 'fortnightly';
    const d = t.days || '';
    if (d === 'mon,tue,wed,thu,fri,sat,sun') return 'daily';
    if (d === 'mon,tue,wed,thu,fri') return 'weekdays';
    if (d === 'sat,sun') return 'weekends';
    if (d && !d.includes(',')) return 'weekly'; // single day = weekly
    return 'daily';
  };

  useEffect(() => {
    if (editingAgent) {
      setName(editingAgent.name || '');
      setDescription(editingAgent.description || '');
      setActions(editingAgent.actions || []);
      const t = editingAgent.trigger || { type: 'cron', hour: 9, minute: 0, days: 'mon,tue,wed,thu,fri,sat,sun' };
      setTrigger(t);
      setFrequency(deriveFrequency(t));
      setStep('configure');
    }
  }, [editingAgent]);

  const handleTemplateSelect = (templateId) => {
    const tmpl = templates.find(t => t.template_id === templateId);
    if (!tmpl) return;
    setName(tmpl.name);
    setDescription(tmpl.description);
    const t = tmpl.trigger || { type: 'cron', hour: 9, minute: 0, days: 'mon,tue,wed,thu,fri,sat,sun' };
    setTrigger(t);
    setFrequency(deriveFrequency(t));
    setStep('configure');
    // Use full action data from template (including params)
    const fullActions = (tmpl.actions || []).map(a => ({
      type: a.type,
      name: a.name,
      params: a.params || {},
    }));
    setActions(fullActions);
  };

  const handleCustom = () => {
    setName('');
    setDescription('');
    setActions([{ type: 'check_bills_due', name: 'Check Bills Due', params: { days_ahead: 3 } }]);
    setTrigger({ type: 'cron', hour: 9, minute: 0, days: 'mon,tue,wed,thu,fri,sat,sun' });
    setStep('configure');
  };

  const addAction = () => {
    setActions([...actions, { type: 'check_bills_due', name: 'Check Bills Due', params: {} }]);
  };

  const updateAction = (idx, updated) => {
    const next = [...actions];
    next[idx] = updated;
    setActions(next);
  };

  const removeAction = (idx) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  const moveAction = (idx, dir) => {
    const next = [...actions];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setActions(next);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      description: description.trim(),
      trigger: { ...trigger, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
      actions,
    };
    if (editingAgent) {
      payload.agent_id = editingAgent.agent_id;
    }
    // If template was used and this is a new agent, include template_id
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 sm:pt-16" data-testid="agent-builder-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f2f4f6] flex-shrink-0">
          <h2 className="font-bold text-base text-[#2d3338]">
            {editingAgent ? 'Edit Routine' : step === 'choose' ? 'New Routine' : 'Configure Routine'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#f2f4f6] transition-colors">
            <X size={18} className="text-[#596065]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {step === 'choose' && (
            <TemplateSelector
              templates={templates}
              onSelect={handleTemplateSelect}
              onCustom={handleCustom}
            />
          )}

          {step === 'configure' && (
            <>
              {/* Name & Description */}
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider block mb-1">Routine Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Morning Briefing"
                    className="w-full text-sm border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 outline-none focus:border-[#005bc1] transition-colors"
                    data-testid="agent-name-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider block mb-1">Description</label>
                  <input
                    type="text"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="What does this routine do?"
                    className="w-full text-sm border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 outline-none focus:border-[#005bc1] transition-colors"
                    data-testid="agent-description-input"
                  />
                </div>
              </div>

              {/* Schedule */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={14} className="text-[#596065]" />
                  <span className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Schedule</span>
                  <LiveClock />
                </div>
                <div className="space-y-2.5">
                  {/* Time */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-[#596065] w-16">Time:</label>
                    <input
                      type="time"
                      value={`${String(trigger.hour || 9).padStart(2, '0')}:${String(trigger.minute || 0).padStart(2, '0')}`}
                      onChange={e => {
                        const [h, m] = e.target.value.split(':').map(Number);
                        setTrigger({ ...trigger, hour: h, minute: m });
                      }}
                      className="text-xs border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1]"
                      data-testid="agent-schedule-time"
                    />
                  </div>
                  {/* Frequency */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-[#596065] w-16">Repeat:</label>
                    <select
                      value={frequency}
                      onChange={e => {
                        const f = e.target.value;
                        setFrequency(f);
                        const opt = FREQUENCY_OPTIONS.find(o => o.value === f);
                        if (f === 'monthly') {
                          setTrigger(prev => ({ ...prev, days: undefined, day_of_month: prev.day_of_month || 1, week_interval: undefined }));
                        } else if (f === 'fortnightly') {
                          setTrigger(prev => ({ ...prev, days: opt.days, day_of_month: undefined, week_interval: 2 }));
                        } else if (f === 'weekly') {
                          setTrigger(prev => ({ ...prev, days: prev.days && !prev.days.includes(',') ? prev.days : 'mon', day_of_month: undefined, week_interval: undefined }));
                        } else {
                          setTrigger(prev => ({ ...prev, days: opt.days, day_of_month: undefined, week_interval: undefined }));
                        }
                      }}
                      className="text-xs border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1]"
                      data-testid="agent-schedule-frequency"
                    >
                      {FREQUENCY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Weekly day picker */}
                  {(frequency === 'weekly' || frequency === 'fortnightly') && (
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs text-[#596065] w-16">On:</label>
                      <div className="flex gap-1">
                        {WEEKLY_DAY_OPTIONS.map(d => (
                          <button
                            key={d.value}
                            type="button"
                            onClick={() => setTrigger(prev => ({ ...prev, days: d.value }))}
                            className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                              trigger.days === d.value
                                ? 'bg-[#0f172a] text-white'
                                : 'bg-[#f2f4f6] text-[#596065] hover:bg-[#e2e8f0]'
                            }`}
                            data-testid={`day-pick-${d.value}`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Monthly day picker */}
                  {frequency === 'monthly' && (
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs text-[#596065] w-16">Day:</label>
                      <select
                        value={trigger.day_of_month || 1}
                        onChange={e => setTrigger(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                        className="text-xs border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1]"
                        data-testid="agent-schedule-day-of-month"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of month</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Action Steps ({actions.length})</span>
                </div>
                <div className="space-y-2">
                  {actions.map((action, i) => (
                    <ActionStepEditor
                      key={i}
                      action={action}
                      index={i}
                      total={actions.length}
                      onChange={updated => updateAction(i, updated)}
                      onRemove={() => removeAction(i)}
                      onMoveUp={() => moveAction(i, -1)}
                      onMoveDown={() => moveAction(i, 1)}
                    />
                  ))}
                </div>
                <button
                  onClick={addAction}
                  className="mt-2 w-full p-2 rounded-lg border-2 border-dashed border-[#e2e8f0] text-[#596065] text-xs font-semibold hover:border-[#005bc1] hover:text-[#005bc1] transition-colors"
                  data-testid="add-action-btn"
                >
                  <Plus size={12} className="inline mr-1" /> Add Step
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {step === 'configure' && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-[#f2f4f6] flex-shrink-0 bg-[#fafbfc]">
            {!editingAgent && (
              <button
                onClick={() => setStep('choose')}
                className="text-xs font-semibold text-[#596065] hover:text-[#2d3338] transition-colors"
                data-testid="back-to-templates-btn"
              >
                Back
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[#596065] hover:bg-[#f2f4f6] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!name.trim() || actions.length === 0 || saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] transition-colors disabled:opacity-40"
                data-testid="save-agent-btn"
              >
                <FloppyDisk size={14} />
                {saving ? 'Saving...' : editingAgent ? 'Update Routine' : 'Create Routine'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const t = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.split('/').pop()?.replace('_', ' ') || '';
  return (
    <span className="ml-auto text-[10px] text-[#acb3b8] tabular-nums" data-testid="live-clock">
      Now: {t} {tz && `(${tz})`}
    </span>
  );
}
